import { useCallback, useEffect, useState } from 'react'

// Cooldown del banner automático de instalación: si el usuario toca
// "Ahora no" no se le vuelve a mostrar el banner durante 7 días. El botón
// manual "Instalar aplicación" del menú NO respeta este cooldown, sigue
// disponible siempre.
const CLAVE_COOLDOWN = 'francofrutt_pwa_install_dismissed_at'
const COOLDOWN_DIAS = 7
const COOLDOWN_MS = COOLDOWN_DIAS * 24 * 60 * 60 * 1000

// iPadOS 13+ se identifica en el userAgent como "Macintosh" (idéntico a un
// Mac de escritorio), la única forma de distinguirlo es que sí soporta
// touch. Un notebook/PC táctil normal no cumple el resto de condiciones
// (userAgent de Mac + Safari), así que esto no da falsos positivos.
function esIpadOsComoMac() {
  if (typeof window === 'undefined') return false

  const ua = window.navigator.userAgent || ''
  return /macintosh/i.test(ua) && navigator.maxTouchPoints > 1
}

function detectarIOS() {
  if (typeof window === 'undefined') return false

  const ua = window.navigator.userAgent || ''
  const esIphoneOIpad = /iphone|ipad|ipod/i.test(ua)

  return esIphoneOIpad || esIpadOsComoMac()
}

// Detección de dispositivo móvil/tablet basada exclusivamente en
// userAgent (Android/iPhone/iPad/iPod + el caso especial de iPadOS). A
// propósito NO se usa el ancho de ventana: un PC/notebook con la
// ventana angosta no debe contar como móvil.
function detectarMobileDevice() {
  if (typeof window === 'undefined') return false

  const ua = window.navigator.userAgent || ''
  const esAndroid = /android/i.test(ua)
  const esIOSDevice = /iphone|ipad|ipod/i.test(ua)

  return esAndroid || esIOSDevice || esIpadOsComoMac()
}

function detectarStandalone() {
  if (typeof window === 'undefined') return false

  const modoStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches || false
  const standaloneIOS = window.navigator.standalone === true

  return modoStandalone || standaloneIOS
}

function leerCooldownActivo() {
  try {
    const guardado = window.localStorage.getItem(CLAVE_COOLDOWN)
    if (!guardado) return false

    const fecha = Number(guardado)
    if (!Number.isFinite(fecha)) return false

    return Date.now() - fecha < COOLDOWN_MS
  } catch {
    // localStorage puede fallar en navegación privada; no es crítico,
    // simplemente no aplicamos cooldown.
    return false
  }
}

// Hook reutilizable con toda la lógica de instalación PWA: detecta
// Android/Chrome (beforeinstallprompt) e iOS Safari (sin ese evento),
// si la app ya corre standalone, y maneja el cooldown del banner. Tanto
// el botón del menú como el banner de instalación se apoyan en este
// mismo hook para no duplicar lógica.
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [instalado, setInstalado] = useState(detectarStandalone)
  const [esIOS] = useState(detectarIOS)
  const [isMobileDevice] = useState(detectarMobileDevice)
  const [cooldownActivo, setCooldownActivo] = useState(
    leerCooldownActivo
  )

  useEffect(() => {
    const alCapturarPrompt = (event) => {
      // Evita que Chrome muestre su propio mini-banner automático; lo
      // disparamos nosotros cuando el usuario presiona nuestro botón.
      event.preventDefault()

      // En desktop (Windows/Mac/Linux) Chrome/Edge también disparan este
      // evento, pero la instalación PWA de Franco Frutt es solo para
      // móvil/tablet: ni siquiera guardamos el evento diferido, así la UI
      // no tiene forma de mostrar nada en escritorio.
      if (!isMobileDevice) return

      setDeferredPrompt(event)
    }

    const alInstalar = () => {
      setInstalado(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', alCapturarPrompt)
    window.addEventListener('appinstalled', alInstalar)

    const mediaQuery = window.matchMedia?.(
      '(display-mode: standalone)'
    )
    const alCambiarDisplayMode = (event) => {
      if (event.matches) setInstalado(true)
    }
    mediaQuery?.addEventListener?.('change', alCambiarDisplayMode)

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        alCapturarPrompt
      )
      window.removeEventListener('appinstalled', alInstalar)
      mediaQuery?.removeEventListener?.(
        'change',
        alCambiarDisplayMode
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileDevice])

  // Instalable si Chrome/Android ya capturó el evento diferido, o si es
  // iOS Safari (ahí no hay evento: se muestran instrucciones manuales) —
  // y en ambos casos, solo en móvil/tablet. En desktop `deferredPrompt`
  // ni siquiera llega a guardarse (ver alCapturarPrompt más arriba), pero
  // se deja `isMobileDevice` explícito acá también como segunda barrera.
  const puedeInstalar =
    isMobileDevice &&
    !instalado &&
    (Boolean(deferredPrompt) || esIOS)

  const instalar = useCallback(async () => {
    if (!deferredPrompt) return null

    deferredPrompt.prompt()
    const eleccion = await deferredPrompt.userChoice
    setDeferredPrompt(null)

    return eleccion
  }, [deferredPrompt])

  const posponer = useCallback(() => {
    try {
      window.localStorage.setItem(CLAVE_COOLDOWN, String(Date.now()))
    } catch {
      // No es crítico si no se puede guardar; el banner podría volver a
      // aparecer en la próxima carga, pero no rompe nada.
    }

    setCooldownActivo(true)
  }, [])

  return {
    instalado,
    esIOS,
    isMobileDevice,
    deferredPrompt,
    puedeInstalar,
    cooldownActivo,
    instalar,
    posponer,
  }
}
